import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Table } from 'reactstrap';

import { formatNumber, formatMs } from '../helpers/format';
import { formatLoadPercent } from '../helpers/externalApiCost';
import { analyzeReplicas, replicaLabel, isKnownReplica, REPLICA_THRESHOLDS } from '../helpers/externalApiReplicas';

/**
* "Replicas" section of the Load tab (VD-725): how the swarm splits the
* external API traffic, one row per replica (swarm slot). A replica that
* gets much more than its fair share, is slower / errors more than the
* others, or has a lagging event loop is flagged - with an icon and a
* reason, never color alone. Clicking a row opens the logs for that replica.
*
* Color: one measure across replicas, so the bars are the single sequential
* blue; a flagged replica's bar takes the reserved status color that matches
* its flag in the table.
*/

const palette = {
    surface: '#FFFFFF',
    border: '#E6EDF5',
    textPrimary: '#3A3F56',
    textSecondary: '#52514E',
    textMuted: '#818FA5',
    bar: '#2a78d6',
    warning: '#fab219',
    critical: '#d03b3b',
};

const SEVERITY = {
    warning: { icon: '▲', color: '#a86f00', bar: palette.warning, rowBg: '#FFF8E6' },
    critical: { icon: '●', color: palette.critical, bar: palette.critical, rowBg: '#FDEEEE' },
};

const styles = {
    card: {
        backgroundColor: palette.surface, borderRadius: 6, padding: '16px 18px 8px 18px',
        boxShadow: '0 10px 20px rgba(0,0,0,0.06)', marginBottom: 20,
    },
    title: { fontSize: 13, fontWeight: 700, color: palette.textPrimary, marginBottom: 4 },
    sub: { fontSize: 12, color: palette.textMuted, marginBottom: 10 },
    issue: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, marginRight: 10, whiteSpace: 'nowrap' },
    muted: { color: palette.textMuted },
};

const dash = (v, fmt) => (v === null || v === undefined ? '—' : fmt(v));
const pct = (v) => dash(v, formatLoadPercent);
const ms = (v) => dash(v, formatMs);
const fixed1 = (v) => dash(v, (x) => Number(x).toFixed(1));

export function DistributionChart({ rows, idealPercent, lang }) {
    let known = rows.filter((r) => isKnownReplica(r.replica));
    let labels = known.map((r) => replicaLabel(r.replica, lang));

    let data = {
        labels,
        datasets: [
            {
                type: 'line',
                label: `${'Ideal'.translate(lang)} (${formatLoadPercent(idealPercent)})`,
                data: known.map(() => idealPercent),
                borderColor: palette.textMuted,
                borderWidth: 1.5,
                borderDash: [6, 4],
                pointRadius: 0,
                fill: false,
                order: 0,
            },
            {
                type: 'bar',
                label: 'Share of requests'.translate(lang),
                data: known.map((r) => r.sharePercentOfKnown),
                backgroundColor: known.map((r) => (r.issues.some((i) => i.reason === 'Uneven distribution') ? SEVERITY.warning.bar : palette.bar)),
                borderRadius: { topLeft: 4, topRight: 4 },
                borderSkipped: false,
                maxBarThickness: 48,
                order: 1,
            },
        ],
    };

    let options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top', align: 'start', labels: { usePointStyle: true, boxWidth: 8, color: palette.textSecondary, font: { size: 12 } } },
            tooltip: {
                callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: ${formatLoadPercent(ctx.parsed.y)}`,
                    afterLabel: (ctx) => {
                        if (ctx.dataset.type !== 'bar') return '';
                        let row = known[ctx.dataIndex];
                        return `${'Requests'.translate(lang)}: ${formatNumber(row.requests)}`;
                    },
                },
            },
        },
        scales: {
            x: { grid: { display: false }, ticks: { color: palette.textMuted, font: { size: 11 } } },
            y: { beginAtZero: true, grid: { color: palette.border }, ticks: { color: palette.textMuted, callback: (v) => `${v}%`, font: { size: 11 } } },
        },
    };

    return (
        <div style={{ height: 240 }}>
            <Bar data={data} options={options} />
        </div>
    );
}

function Issues({ issues, lang }) {
    if (!issues.length) {
        return <span style={styles.muted}>{'OK'.translate(lang)}</span>;
    }

    // the same reason can come from two checks (avg duration and slow %), list it once
    let seen = {};
    return issues.filter((i) => (seen[i.reason] ? false : (seen[i.reason] = true))).map((i) => (
        <span key={i.reason} style={{ ...styles.issue, color: SEVERITY[i.severity].color }}>
            <span aria-hidden="true">{SEVERITY[i.severity].icon}</span>
            {i.reason.translate(lang)}
        </span>
    ));
}

/**
* @param {object[]} byReplica - the `/stats` `byReplica` rows
* @param {function} [onOpenReplica] - called with the slot number when a row is clicked
*/
function ExternalApiReplicas({ byReplica, onOpenReplica, lang }) {
    if (!byReplica || !byReplica.length) {
        return null;
    }

    let { rows, replicaCount, idealPercent } = analyzeReplicas(byReplica);
    let hasOldLogs = rows.some((r) => !isKnownReplica(r.replica));
    let t = REPLICA_THRESHOLDS;

    return (
        <div style={styles.card}>
            <div style={styles.title}>{'Replicas'.translate(lang)}</div>
            <div style={styles.sub}>
                {replicaCount
                    ? `${replicaCount} ${(replicaCount === 1 ? 'replica' : 'replicas').translate(lang)} ${'in the selected period.'.translate(lang)}`
                    : 'No replica data in the selected period yet - only requests made after the replica tracking was deployed have it.'.translate(lang)}
                {hasOldLogs && replicaCount ? ` ${'The distribution is calculated among known replicas only, without old logs.'.translate(lang)}` : ''}
            </div>

            {replicaCount >= 2 ? <DistributionChart rows={rows} idealPercent={idealPercent} lang={lang} /> : null}

            <Table responsive hover size="sm" style={{ fontSize: 13, marginTop: 12 }}>
                <thead>
                    <tr>
                        <th>{'Replica'.translate(lang)}</th>
                        <th>{'Requests'.translate(lang)}</th>
                        <th title={'Share of the total processing time'.translate(lang)}>{'Time share'.translate(lang)}</th>
                        <th title={'Busy time of this replica alone (processing time / period) - not the cluster-wide load'.translate(lang)}>{'Replica load'.translate(lang)}</th>
                        <th>{'Avg duration'.translate(lang)}</th>
                        <th title={`>= ${formatMs(t.slowRequestMs)}`}>{'Slow %'.translate(lang)}</th>
                        <th>{'Errors %'.translate(lang)}</th>
                        <th>5xx</th>
                        <th title={'Other requests being processed on the replica when a request arrived'.translate(lang)}>{'In flight avg / max'.translate(lang)}</th>
                        <th title={'Event loop lag'.translate(lang)}>{'Lag avg / max'.translate(lang)}</th>
                        <th>{'Status'.translate(lang)}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        let known = isKnownReplica(row.replica);
                        let clickable = known && !!onOpenReplica;
                        let sev = row.severity ? SEVERITY[row.severity] : null;

                        return (
                            <tr
                                key={known ? row.replica : 'unknown'}
                                style={{
                                    cursor: clickable ? 'pointer' : 'default',
                                    color: known ? undefined : palette.textMuted,
                                    backgroundColor: sev ? sev.rowBg : undefined,
                                }}
                                title={clickable ? 'Open the logs for this replica'.translate(lang) : undefined}
                                onClick={() => clickable && onOpenReplica(row.replica)}
                            >
                                <td style={{ fontWeight: 600 }}>{replicaLabel(row.replica, lang)}</td>
                                <td>
                                    {formatNumber(row.requests)}
                                    <span style={{ ...styles.muted, fontSize: 11 }}> ({pct(row.requestsPercent)})</span>
                                </td>
                                <td>{pct(row.sharePercent)}</td>
                                <td>{pct(row.replicaLoadPercent)}</td>
                                <td>{ms(row.avgDurationMs)}</td>
                                <td>{pct(row.slowPercent)}</td>
                                <td>{pct(row.errorPercent)}</td>
                                <td>{dash(row.serverErrors, formatNumber)}</td>
                                <td>{fixed1(row.avgInFlight)} / {dash(row.maxInFlight, formatNumber)}</td>
                                <td>{ms(row.avgEventLoopLagMs)} / {ms(row.maxEventLoopLagMs)}</td>
                                <td>{known ? <Issues issues={row.issues} lang={lang} /> : <span>—</span>}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>

        </div>
    );
}

export default ExternalApiReplicas;
